// move-handlers.ts — XZ drag-drop MOVE handlers (template.js cleanup roadmap T1.2, 2026-06-14).
//
// Byte-faithful port of the drag/drop ACTION-orchestration layer that stayed behind in template.js
// These are the drop -> POST -> refresh handlers the inventory and FT3D movement surfaces invoke.
//
// Public surface (window-attached at the bottom — the encounter-map bundle, which loads BEFORE
// template.js, reaches these as bare-identifier globals at runtime):
//   encounter_creatures_moved, encounter_linked_creatures_moved, move_draggable_inventory,
//   gmMoveCreatureCrossEncounterAtGrid, refreshGmViewerAfterCreatureMove, getGmDragSourceEncounterId,
//   getAoeSpatialReferenceEncounterCreatureId, inventoryItemElIsExcludedFromAoe.
//
// MAP_MARKER_INVENTORY_TEMPLATE_IDS is used only by inventoryItemElIsExcludedFromAoe here, so it
// stays module-local (grep confirmed zero external callers).
//
// Loaded AFTER template.js, so the template.js / core-helpers globals these invoke (queryData,
// BASE_URL, isEmpty, displayWarning, clearLegacySelection, ftDebugLog, checkIfValidUUID, getCookieValue,
// openItemInfo, removeInventorySliderOverlay, __ftShouldQuietRefreshSideInfoPanel) resolve at call
// time — every function here runs only in response to a drag/drop, long after template.js parses.

import {
    applyInventoryMoveReceiptToRoots,
    type InventoryMoveReceipt,
} from './inventory-move-receipt';
// Janus C1 shadow envelope (plans/janus-c1-possessor-compile-plan-2026-09-15.md §8.3): the browser's possessor compile rides
// the legacy POST as an OPTIONAL field; the server keeps computing as today and only logs the parity.

/** The move endpoint refused the claim as compiled from a stale row (409 why c1-stale:*), before any write (N6 / N4 ask 1). */
class InventoryMoveC1Stale extends Error {
    constructor(readonly why: string) { super('c1-stale'); }
}
import { actorCanReachTarget, actorObjectInteractionRangeFt, getProximityEntity } from '../shared/proximity-browser';
import { inventoryMoveProximity } from '../shared/inventory-proximity';
import { carriedRetryMatches, decideInventoryMove, decideTakeAll, sameMoveDecision, type ClientMoveDecision, type ClientTakeAllDecision } from './move-decision';
import { refuseUnported } from '../janus-engine/unported.js';
import { position, rowScalar } from '../encounter-map/ft3d/perception';
import type { ServerDropContext } from '../janus-engine/inventory/server-drop';
import { ftCurrentTruthSink, isTruthSinkRefusal } from '../shared/truth-sink';
import type { MemoryPickupReceipt } from '../janus-engine/inventory/memory-pickup';
import { awaitInventoryBirth, INVENTORY_BIRTH_WAIT_WORDS } from '../shared/inventory-birth-persistence';
import { withAsyncDeadline } from '../shared/async-deadline';
import { publishInventoryMoveReceipt, publishInventoryMoveBatch } from '../shared/inventory-applied-truth';
import type { InventoryMoveTails } from '../janus-engine/inventory/delete-plan';

/**
 * MV5 (2026-10-02) — the bag-equip chain's asks (N6c / N6d) on window.ftMoveInventoryWithReceipt, as ONE optional trailing argument:
 *  - `requiredDecision` (ask a): the decision the caller already checked (decideInventoryMove). The move re-decides as always (after its reach
 *    check, a stale-claim retry…) and REFUSES when that differs (another merge target, another AP block) — it never re-decides into a merge.
 *  - `apLeg: 'fast-draw'` (ask c): a 0-AP leg drawing an item out of a bag into the actor's hands. Whether Fast Draw applies is the caller's
 *    rule (equip-rules.ts); the chain still charges the move's 1 AP today — worker pick under review, unchanged here.
 *  - `splitInPlace` (I2/I3 ask): split `item_qty` off a stack the actor holds DIRECTLY into a NEW row under the same holder (0 AP; the equip
 *    that follows pays) — the receipt's `move.destination.inventory_id` is the new row. Posts no C1 claim (the server rebuilds the carrier).
 */
export interface InventoryMoveOptions {
    requiredDecision?: ClientMoveDecision;
    apLeg?: 'standard' | 'fast-draw';
    splitInPlace?: boolean;
}
/** MV5: a move refused BEFORE anything was written — by the page's rules, by a changed `requiredDecision`, or by the server's pre-write C1
 *  refusal (409 `c1-stale:refused:<reason>`, N4c — never retried). `message` is the player-facing words (also shown unless `quiet`). */
export interface InventoryMoveRefusal {
    schema: 'ft3d.inventory-move-refusal.v1';
    ok: false;
    refused: string;
    message: string;
}
/** MV5: the first attempt of each CARRIED mutation id (a caller that may retry a lost answer — the bag-equip chain): its decision and row
 *  CAS, re-sent verbatim by a retry. Kept 660 s (past the endpoint's 600 s receipt window — N6e); at most 64. */
const CARRIED_ATTEMPT_TTL_MS = 660_000;
/** How long a move waits for its pick-up trap fire before posting the plain payload (MV3, coordinator round 18 P3). */
const BODY_BUILD_DEADLINE_MS = 15_000;
/** `leg`: the first attempt's move kind (split in place / Fast Draw / standard) — a retry asking for another kind is refused (MV2).
 *  `body`: the first attempt's SERIALIZED request, set ONCE as a promise before any await (MV2, Codex round 11 P2): overlapping calls that
 *  share the mutation id all post that same first body — the first body wins; the endpoint answers a different body 409 anyway. */
type CarriedMoveAttempt = { decision: ClientMoveDecision | null; expectedHolder: string; leg: string; at: number; body?: Promise<string> };
const carriedMoveAttempts = new Map<string, CarriedMoveAttempt>();
function rememberCarriedMoveAttempt(id: string, decision: ClientMoveDecision | null, expectedHolder: string, leg: string): void {
    const now = Date.now();
    for (const [key, entry] of carriedMoveAttempts) if (now - entry.at > CARRIED_ATTEMPT_TTL_MS || carriedMoveAttempts.size >= 64) carriedMoveAttempts.delete(key);
    carriedMoveAttempts.set(id, { decision: decision ? { target: decision.target, ap: { ...decision.ap } } : null, expectedHolder, leg, at: now });
}
/** The move kind a call asks for — part of what a carried retry must repeat. */
function carriedMoveLeg(options: InventoryMoveOptions | null | undefined): string {
    return options?.splitInPlace === true ? 'split-in-place' : options?.apLeg === 'fast-draw' ? 'fast-draw' : 'standard';
}
/** A caller WITHOUT move options keeps the legacy contract (Codex MV5 round 2 P3): the warning always shows and the answer is null. */
function moveRefusal(refused: string, message: string, quiet: boolean, legacy: boolean): InventoryMoveRefusal | null {
    if (legacy || !quiet) displayWarning(message);
    return legacy ? null : { schema: 'ft3d.inventory-move-refusal.v1', ok: false, refused, message };
}
/** The endpoint answered (not a lost answer): `status` is its HTTP status. */
class InventoryMoveHttpError extends Error {
    /** `code`: the endpoint's `error` (MV3, coordinator round 18 P3 — an in-flight / id-conflict 409 must not drop the remembered first body). */
    constructor(message: string, readonly status: number, readonly code = '') { super(message); }
}
/** N4c's rule for a pre-write `c1-stale:refused:<reason>`: a TRANSIENT compile refusal (`client-timeout`, `def-missing` — the page's
 *  compile ran out of time or had not fetched a definition yet) is retried ONCE like a stale claim; any other reason is a refusal (its
 *  words shown, never retried — a retry would compile the same refusal). */
function c1RefusalReason(why: string): string | null {
    if (!why.startsWith('c1-stale:refused:')) return null;
    const reason = why.slice('c1-stale:refused:'.length);
    return /^(client-timeout|def-missing)(:|$)/.test(reason) ? null : reason;
}
// ===========================================================================================
// Map marker template item_ids — keep in sync with MapMarker::LIST in includes/additions.php
// ===========================================================================================
const MAP_MARKER_INVENTORY_TEMPLATE_IDS = new Set([
    'C7814403-F84C-F6F9-0794-95F3EC3AE394',
    'F5DDC0AA-581A-8E6F-EFAF-4445D1A3EBC3',
    'ACE453E8-5725-AB18-0BCF-589DDA35135D',
    'BF941177-6C2F-4EF6-92C8-BF45E0E1BE52',
    '85436C19-426D-491A-E049-42731CF8AB70',
    'D93EF021-9229-85C0-46DB-43F0E7E0FE9D',
    '73F0A293-AE4D-5E0C-9A0A-59F46FE20F5D',
    '44B43903-216C-386A-24BE-5DE3BCBB8E0D',
]);

interface XzSpatialWidget {
    elements?: { canvas?: ParentNode | null };
}

interface XzLeafletElement extends HTMLElement {
    _ftLeafletMap?: { closePopup?: () => void };
}

function moveRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

/** Inventory nodes to skip for AOE overlap (XZ often renders via renderInventoryThumbnail() without older PHP-only attrs). */
function inventoryItemElIsExcludedFromAoe(el: unknown): boolean {
    if (!(el instanceof HTMLElement) || !el.classList.contains('inventory-item')) return false;
    if (el.getAttribute('data-item_type') === 'map_marker') return true;
    const tid = (el.getAttribute('data-inventory_item_id') || '').trim().toUpperCase();
    return tid !== '' && MAP_MARKER_INVENTORY_TEMPLATE_IDS.has(tid);
}

/**
 * Encounter creature whose token anchors AOE placement.
 *
 * Primary source: window.FateTide.activeViewerCasterId — kept in sync by subscribeToCaster()
 * from BOTH main-sheet opens (attemptOpenItemInfo) AND summon-tab clicks (setCasterSummon),
 * so it reliably tracks whichever caster sheet the GM/player is currently operating on.
 *
 * The legacy fallback below reconciles the master/active caster cache, the active summon tab CSS
 * and the per-tab selected-summon store (ftGetSelectedSummon; sessionStorage, de-cookie 2026-06-24) —
 * only reached when the cache value is absent (very early first paint before any caster open, or
 * detached/legacy contexts). The GM viewer bit
 * here on 2026-05-26: #caster_main_id is empty at page load and the .active summon-tab CSS
 * could persist across sheet swaps, causing AOEs to anchor on a stale summon.
 */
function getAoeSpatialReferenceEncounterCreatureId(widget: XzSpatialWidget | null | undefined): string {
    const activeId = window.FateTide?.activeViewerCasterId;
    if (activeId && checkIfValidUUID(activeId)) return activeId;

    const domCaster = String(window.ftGetActiveCasterId?.() ?? '').trim();
    const mainId = String(window.ftGetMasterCasterId?.() ?? '').trim();

    if (!domCaster) return domCaster;

    const canvas = widget?.elements?.canvas;
    const tokenFor = (id: unknown) =>
        id && checkIfValidUUID(id)
            ? canvas?.querySelector(`.encounter-creature[data-encounter_creature_id="${id}"]`)
            : null;

    if (mainId && domCaster !== mainId) {
        return domCaster;
    }

    const activeTab = document.querySelector('.btn_caster_summons_tab.active');
    if (activeTab?.id?.startsWith('tab_')) {
        const sid = activeTab.id.slice('tab_'.length);
        if (tokenFor(sid)) return sid;
    }

    const ck = window.ftGetSelectedSummon?.() || ''; // per-tab sessionStorage (de-cookie 2026-06-24)
    const c = String(ck).trim();
    if (c && c !== mainId && tokenFor(c)) return c;

    return domCaster;
}

function getGmDragSourceEncounterId(el: HTMLElement | null | undefined): string {
    if (!el) {
        return '';
    }
    const d = el.dataset.gmSourceEncounterId;
    if (d) {
        return d;
    }
    const dz = el.closest<HTMLElement>('[data-dropzone_id]');
    if (dz && dz.dataset.dropzoneId) {
        return dz.dataset.dropzoneId;
    }
    return '';
}

function rowField(row: unknown, bucketName: string, key: string): string {
    const record = moveRecord(row);
    if (!record) return '';
    const bucket = moveRecord(record[bucketName]);
    let value = bucket && key in bucket ? bucket[key] : record[key];
    if (Array.isArray(value)) value = value[0];
    const valueRecord = moveRecord(value);
    const items = moveRecord(valueRecord?.items);
    if (typeof items?.item_id === 'string') {
        return items.item_id;
    }
    return value == null ? '' : String(value);
}

function encounterIdFromArr(encounterArr: unknown): string {
    return rowField(encounterArr, 'encounters', 'encounter_id');
}

function resolveCampaignIdFromCache(): string {
    const resolved = window.ftGetCampaignId?.();
    if (resolved) return String(resolved);

    const ft = window.FateTide || {};
    const direct = ft.campaign?.id || ft.campaignArr?.campaigns?.campaign_id || ft.currentCampaignArr?.campaigns?.campaign_id;
    if (direct) return String(direct);

    const cache = ft.campaignCache;
    if (cache instanceof Map && cache.size === 1) {
        const first = cache.values().next().value;
        return String(first?.campaigns?.campaign_id || first?.campaign_id || '');
    }

    return '';
}

interface XzMoveEncounterContext {
    campaign_id?: string;
    encounter_id?: string;
    [key: string]: unknown;
}

function resolveEncounterContext(opts: { encounterId?: string } = {}): XzMoveEncounterContext {
    const context = window.ftGetEncounterContext?.(opts);
    return context && typeof context === 'object' && !Array.isArray(context)
        ? context as XzMoveEncounterContext
        : {};
}

function cachedEncounterContainsPossessId(encounterArr: unknown, id: string): boolean {
    if (!id) return false;
    const encounter = moveRecord(encounterArr);
    const creatures = Array.isArray(encounter?.encounter_creatures_arr)
        ? encounter.encounter_creatures_arr
        : [];
    if (creatures.some((row) => {
        const rowRecord = moveRecord(row);
        return rowField(rowRecord?.encounter_creatures || row, 'encounter_creatures', 'encounter_creature_id') === id;
    })) {
        return true;
    }

    const inventories = Array.isArray(encounter?.encounter_inventories_arr)
        ? encounter.encounter_inventories_arr
        : [];
    return inventories.some((row) =>
        rowField(row, 'inventory', 'inventory_id') === id
        || rowField(row, 'inventory', 'possess_id') === id
    );
}

function resolveInventoryMoveEncounterId(moveToPossessId: unknown, inventoryId: unknown, moveType: unknown): string {
    const targetId = String(moveToPossessId || '').trim();
    if (moveType === 'drop_inventory' && checkIfValidUUID(targetId)) {
        return targetId;
    }

    const cache = window.FateTide?.encounterCache;
    if (cache instanceof Map) {
        for (const encounterArr of cache.values()) {
            if (cachedEncounterContainsPossessId(encounterArr, targetId) || cachedEncounterContainsPossessId(encounterArr, String(inventoryId || ''))) {
                const eid = encounterIdFromArr(encounterArr);
                if (eid) return eid;
            }
        }
    }

    const movedEl = inventoryId
        ? document.querySelector(`[data-inventory_id="${String(inventoryId).replace(/(["'\\\\\[\]])/g, '\\\\$1')}"]`)
        : null;
    const sceneHost = movedEl?.closest?.('[data-ft3d-campaign-encounter-view]') as HTMLElement | null;
    return String(sceneHost?.dataset?.encounterId || window.ftGetActiveEncounterId?.() || '').trim();
}

function ftInventoryMoveMutationId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().toUpperCase();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const value = Math.floor(Math.random() * 16);
        return (char === 'x' ? value : (value & 0x3) | 0x8).toString(16);
    }).toUpperCase();
}

function ftSetInventoryMovePending(inventoryId: string, mutationId: string, pending: boolean): void {
    const escaped = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        ? CSS.escape(inventoryId)
        : inventoryId.replace(/(["'\\\[\]])/g, '\\$1');
    document.querySelectorAll<HTMLElement>(`.inventory-item[data-inventory_id="${escaped}"]`).forEach((tile) => {
        if (!pending && tile.dataset.ftMoveMutationId !== mutationId) return;
        if (pending) {
            tile.dataset.ftMoveMutationId = mutationId;
            tile.dataset.ftMovePending = '1';
            tile.dataset.ftMovePrevPointerEvents = tile.style.pointerEvents;
            tile.dataset.ftMovePrevOpacity = tile.style.opacity;
            tile.setAttribute('aria-busy', 'true');
            tile.style.pointerEvents = 'none';
            tile.style.opacity = '0.58';
        } else {
            delete tile.dataset.ftMoveMutationId;
            delete tile.dataset.ftMovePending;
            tile.removeAttribute('aria-busy');
            if (tile.dataset.ftMovePrevPointerEvents) tile.style.pointerEvents = tile.dataset.ftMovePrevPointerEvents;
            else tile.style.removeProperty('pointer-events');
            if (tile.dataset.ftMovePrevOpacity) tile.style.opacity = tile.dataset.ftMovePrevOpacity;
            else tile.style.removeProperty('opacity');
            delete tile.dataset.ftMovePrevPointerEvents;
            delete tile.dataset.ftMovePrevOpacity;
        }
    });
}

function ftInventoryMoveCachedRoots(): unknown[] {
    const ft = window.FateTide;
    const roots: unknown[] = [];
    for (const cache of [ft?.casterCache, ft?.encounterCache, ft?.objectCache]) {
        if (cache instanceof Map) roots.push(...cache.values());
    }
    if (ft?.encounterArr) roots.push(ft.encounterArr);
    return Array.from(new Set(roots));
}

function ftIsInventoryMoveReceipt(value: unknown): value is InventoryMoveReceipt {
    const receipt = moveRecord(value);
    const move = moveRecord(receipt?.move);
    const source = moveRecord(move?.source);
    const destination = moveRecord(move?.destination);
    const roots = moveRecord(move?.roots);
    const position = moveRecord(destination?.position);
    return receipt?.schema === 'ft3d.inventory-move-receipt.v1'
        && receipt.ok === true
        && typeof receipt.mutation_id === 'string'
        && ['full_move', 'full_merge', 'partial_split', 'partial_merge'].includes(String(move?.outcome ?? ''))
        && typeof source?.inventory_id === 'string'
        && typeof source.possess_id_before === 'string'
        && typeof source.item_qty_after === 'number'
        && typeof source.deleted_after === 'boolean'
        && typeof destination?.inventory_id === 'string'
        && typeof destination.possess_id === 'string'
        && typeof destination.item_qty_after === 'number'
        && typeof destination.created === 'boolean'
        && typeof destination.equipped === 'string'
        && typeof position?.x === 'number'
        && typeof position.y === 'number'
        && typeof position.z === 'number'
        && roots !== null
        && Array.isArray(receipt.affected_caster_ids)
        && Array.isArray(receipt.affected_encounter_ids);
}

function ftRenderInventoryMoveFailure(html: string, fallback: string): void {
    const host = document.getElementById('div_sidelog_warning');
    if (host && html.trim() !== '') host.innerHTML = html;
    else if (typeof displayWarning === 'function') displayWarning(fallback);
}

function ftInventoryMoveRetryDelayMs(response: Response | null): number {
    const retryAfter = response?.headers.get('Retry-After')?.trim() ?? '';
    const retryAfterSeconds = Number(retryAfter);
    if (retryAfter !== '' && Number.isFinite(retryAfterSeconds)) {
        return Math.min(1500, Math.max(0, retryAfterSeconds * 1000));
    }
    return response === null ? 200 : 250;
}

/** Lane I2/I3 S3: the holder this page shows the row under — sent as `expected_possess_id` (the server's row CAS). Every cached copy
 *  must agree; when they do not (a fan-out mid-flight on this page) nothing is sent and the server keeps its legacy behaviour. */
function ftInventoryExpectedHolder(inventoryId: string): string {
    const ft = (window as unknown as { FateTide?: { casterCache?: unknown; encounterCache?: unknown; objectCache?: unknown } }).FateTide;
    const holders = new Set<string>();
    // EVERY copy inside every cached root (Codex review S3 #4: a root can hold an old and a new copy under different holders).
    const visit = (node: unknown, depth: number): void => {
        if (depth > 24 || node === null || typeof node !== 'object') return;
        if (!Array.isArray(node)) {
            const bucket = (node as Record<string, unknown>).inventory;
            if (bucket && typeof bucket === 'object' && !Array.isArray(bucket)
                && String(rowScalar(node as Record<string,unknown>,'inventory_id') ?? '').trim().toUpperCase() === inventoryId) {
                const holder = String(rowScalar(node as Record<string,unknown>,'possess_id') ?? '').trim().toUpperCase();
                if (holder) holders.add(holder);
            }
        }
        for (const child of Array.isArray(node) ? node : Object.values(node as Record<string, unknown>)) visit(child, depth + 1);
    };
    for (const cache of [ft?.casterCache, ft?.encounterCache, ft?.objectCache]) {
        if (!(cache instanceof Map)) continue;
        for (const value of cache.values()) visit(value, 0);
    }
    return holders.size === 1 ? [...holders][0]! : '';
}

/** A 409 row-CAS refusal: the page's copy of the row is stale — resync the encounter and the casters the move named. */
function ftHealStaleInventoryMove(payload: Record<string, unknown>): void {
    const encounter = String(payload.encounter_id ?? '');
    if (encounter && typeof window.ftResyncEncounter === 'function') void window.ftResyncEncounter(encounter).catch(() => undefined);
    for (const id of [payload.caster_id, payload.move_to_possess_id, payload.expected_possess_id]) {
        const casterId = String(id ?? '');
        if (checkIfValidUUID(casterId) && casterId !== encounter && typeof window.fetchAndCacheCaster === 'function') {
            void window.fetchAndCacheCaster(casterId, { skipEncounterSeed: true }).catch(() => undefined);
        }
    }
}

/** A pick-up springs any armed pick_up / touch trap on the lifted row (move_draggable_inventory_inc.php's hook): its fire is resolved HERE
 * (trap-truth.ts buildFire — owner 2026-09-29: no trap rolls on the server) and the endpoint records it. Null = nothing on the row fires. */
async function ftPickupTrapFire(payload: Record<string, unknown>): Promise<Record<string, unknown> | null> {
    if (!['move_inventory', 'equip_inventory'].includes(String(payload.move_type ?? ''))) return null;
    const build = (window as Window & { ftTrapBuildFire?: (e: string, h: string, v: string, i: 'pick_up' | 'touch') => Promise<Record<string, unknown> | null> }).ftTrapBuildFire;
    const encounterId = String(payload.encounter_id ?? ''), hostId = String(payload.inventory_id ?? ''), victimId = String(payload.caster_id ?? '');
    if (!build || !checkIfValidUUID(encounterId) || !checkIfValidUUID(hostId) || !checkIfValidUUID(victimId)) return null;
    try { return (await build(encounterId, hostId, victimId, 'pick_up')) ?? (await build(encounterId, hostId, victimId, 'touch')); } catch { return null; }
}
async function ftPostInventoryMove(payloadIn: Record<string, unknown>, carriedId = ''): Promise<InventoryMoveReceipt | null> {
    // MV5 (Codex round 3 P2): a CARRIED mutation id re-sends its first attempt's SERIALIZED request verbatim (trap fire included) — the
    // endpoint's fingerprint then matches and it replays the saved receipt instead of answering 409 mutation_id_conflict.
    // MV2 (Codex round 11 P2): the first body is claimed SYNCHRONOUSLY (a promise stored before any await), so overlapping calls sharing
    // the id cannot each serialize and send their own first body.
    const remembered = carriedId ? carriedMoveAttempts.get(carriedId) : undefined;
    // Never rejects (MV2 review P2): a failure falls back to the plain payload INSIDE the shared promise, so every overlapping call still
    // posts that one first body.
    // MV3 (coordinator round 18 P3): the trap fire has a DEADLINE inside the shared promise — a hung fire can no longer block every overlapping
    // retry; at the deadline the shared first body is the plain payload (still ONE body for every caller).
    const buildBody = async (): Promise<string> => {
        try {
            let timer: ReturnType<typeof setTimeout> | undefined;
            const deadline = new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), BODY_BUILD_DEADLINE_MS); });
            const trapFire = await Promise.race([ftPickupTrapFire(payloadIn), deadline]).finally(() => clearTimeout(timer));
            return JSON.stringify(trapFire ? { ...payloadIn, trap_fire: trapFire } : payloadIn);
        } catch { return JSON.stringify(payloadIn); }
    };
    let bodyPromise: Promise<string>;
    if (remembered) {
        if (!remembered.body) remembered.body = buildBody();
        bodyPromise = remembered.body;
    } else bodyPromise = buildBody();
    let requestBody: string;
    try { requestBody = await bodyPromise; } catch { requestBody = JSON.stringify(payloadIn); }
    let payload: Record<string, unknown> = payloadIn;
    try { payload = JSON.parse(requestBody) as Record<string, unknown>; } catch { requestBody = JSON.stringify(payloadIn); }
    const trapFire = moveRecord(payload.trap_fire);
    if (trapFire?.mode === 'unmodelled') {
        const reason = String(trapFire.reason ?? 'trap-fire:unmodelled');
        refuseUnported(reason);
        throw new InventoryMoveHttpError(reason, 0, reason);
    }
    const trapFrame = trapFire ? window.ftInventoryMoveTrapFrame?.(payload) : undefined;
    if (trapFire && (trapFire.mode !== 'no_fire' || trapFire.maintenance) && !trapFrame)
        throw new InventoryMoveHttpError('inventory-move:trap-frame-unavailable', 0, 'inventory-move:trap-frame-unavailable');
    for (let attempt = 0; attempt < 2; attempt += 1) {
        let response: Response;
        let body: string;
        try {
            response = await withAsyncDeadline(() => fetch(
                BASE_URL + 'options/tools/inventory/ft3d/move_draggable_inventory_inc.php',
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: requestBody,
                },
            ), 20_000);
            body = await withAsyncDeadline(() => response.text(), 20_000);
        } catch (error) {
            if (attempt === 0) {
                ftDebugLog('ft3d inventory move', {
                    mutationId: String(payload.mutation_id ?? ''),
                    stage: 'retry-after-transport-failure',
                });
                await new Promise<void>((resolve) => setTimeout(resolve, ftInventoryMoveRetryDelayMs(null)));
                continue;
            }
            ftRenderInventoryMoveFailure('', 'Inventory move could not be confirmed. Please try again.');
            throw error;
        }

        // Donning/doffing intentionally exits after creating its timed status and processing affected
        // ids; that accepted/deferred path predates receipts and has no inventory mutation to project.
        if (response.ok && body.trim() === '') return null;
        let parsed: unknown = null;
        try { parsed = JSON.parse(body); } catch { /* legacy display_warning HTML */ }
        const errorCode = String(moveRecord(parsed)?.error ?? '');
        const why = String(moveRecord(parsed)?.why ?? '');
        if (response.status === 409 && errorCode === 'stale' && why.startsWith('c1-stale')) throw new InventoryMoveC1Stale(why);
        if (attempt === 0 && response.status === 409 && errorCode === 'mutation_in_progress') {
            const delayMs = ftInventoryMoveRetryDelayMs(response);
            ftDebugLog('ft3d inventory move', {
                mutationId: String(payload.mutation_id ?? ''),
                stage: 'retry-after-mutation-in-progress',
                delayMs,
            });
            await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
            continue;
        }
        if (!response.ok || !ftIsInventoryMoveReceipt(parsed)) {
            if (response.status === 409 && response.headers.get('X-FT-Inventory-Stale') === '1') ftHealStaleInventoryMove(payload);
            ftRenderInventoryMoveFailure(body, response.ok ? 'Inventory move was rejected.' : `Inventory move failed (${response.status}).`);
            throw new InventoryMoveHttpError(`FT3D inventory move did not return a valid receipt (HTTP ${response.status})`, response.status, errorCode);
        }
        await publishInventoryMoveReceipt(parsed, { ...payload, trap_applied_frame: trapFrame }, response);
        const trapVictim = (parsed as { trap_victim?: unknown }).trap_victim;
        if (trapVictim && !parsed.browser_applied) (window as Window & { ftTrapApplyVictim?: (e: string, saved: unknown) => void }).ftTrapApplyVictim?.(String(payload.encounter_id ?? ''), trapVictim);
        return parsed;
    }
    throw new Error('FT3D inventory move retry loop ended unexpectedly');
}

function ftApplyInventoryMoveReceipt(receipt: InventoryMoveReceipt): number {
    const changedRoots = applyInventoryMoveReceiptToRoots(receipt, ftInventoryMoveCachedRoots());
    for (const casterId of receipt.affected_caster_ids) {
        try { window.ftRerenderPcSheet?.(casterId); } catch { /* non-fatal */ }
        try { window.ftRerenderOpenInfoPanel?.(casterId); } catch { /* non-fatal */ }
    }
    for (const encounterId of receipt.affected_encounter_ids) {
        const encounterArr = window.FateTide?.encounterCache?.get(encounterId);
        if (encounterArr) {
            window.dispatchEvent(new CustomEvent('ft:encounter-state-received', {
                detail: { encounterId, encounterArr, subType: 'inventory_move_receipt' },
            }));
        }
    }
    try { window.ftRerenderOpenSideInfo?.(); } catch { /* non-fatal */ }
    window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt-applied', { detail: receipt }));
    return changedRoots;
}

/** The memory sink already installed its rows and clusters. Publish only their affected panes and floor objects. */
function ftPublishLocalInventoryMoves(receipts: MemoryPickupReceipt[]): void {
    if (!receipts.length) return;
    const first = receipts[0]!;
    (window.ftRerenderPcSheet as ((id: string, paths: readonly string[]) => unknown) | undefined)?.(
        first.move.destination.possess_id, first.changed_paths);
    window.dispatchEvent(new CustomEvent('ft:local-inventory-move', { detail: {
        encounterId: first.affected_encounter_ids[0], inventoryIds: receipts.map(receipt => receipt.move.source.inventory_id) } }));
    for (const receipt of receipts) window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt-applied', { detail: receipt }));
}

async function refreshGmViewerAfterCreatureMove(): Promise<void> {
    const context = resolveEncounterContext();
    const campaign_id = context.campaign_id || resolveCampaignIdFromCache();
    const encounter_id = context.encounter_id || '';
    const input_hidden_encounter_link_form = document.getElementById('input_hidden_encounter_link_form');
    if (input_hidden_encounter_link_form) {
        await queryData(
            BASE_URL + 'options/tools/encounters/gm_load_encounter/form_query_campaign_encounter_links_inc.php',
            'div_refresh_gm_viewer',
            { campaign_id, encounter_id },
            true,
            false,
        );
    } else {
        await queryData(
            BASE_URL + 'options/tools/encounters/gm_load_encounter/form_query_campaign_public_encounters_inc.php',
            'div_refresh_gm_viewer',
            { campaign_id },
            true,
            false,
        );
    }
}

async function gmMoveCreatureCrossEncounterAtGrid(draggableId: string, sourceEncounterId: string, targetEncounterId: string, finalX: number, finalZ: number, ftMoved: number): Promise<void> {
    const campaign_id = resolveCampaignIdFromCache();
    await queryData(
        BASE_URL + 'options/tools/encounters/gm_load_encounter/gm_move_creature_cross_encounter_at_grid_inc.php',
        null,
        {
            campaign_id,
            encounter_id: targetEncounterId,
            encounter_creature_id: draggableId,
            source_encounter_id: sourceEncounterId,
            x_pos: finalX,
            z_pos: finalZ,
            ft_moved: ftMoved || 1,
        },
        false,
        false,
    );
    await refreshGmViewerAfterCreatureMove();
    if (typeof clearLegacySelection === 'function') {
        clearLegacySelection();
    }
}

async function encounter_creatures_moved(draggedId: string, dropzoneId: string): Promise<void> {
    ftDebugLog('xz dnd', { encounterCreaturesMoved: { draggedId, dropzoneId } });


    // Map page (view_map.php) doesn't use the `#div_refresh_side_info` shell —
    // creature placement targets the Encounter column.
    // Resolve the dragged element by data-draggable_id so the lookup works
    // regardless of whether the token is `id="<creature_id>"` (legacy GM
    // cards) or `id="ft_marker_token_<creature_id>"` (marker popup).
    let draggedEl: HTMLElement | null = null;
    if (draggedId) {
        draggedEl = document.getElementById(draggedId);
        if (!draggedEl) {
            const safeSel = (typeof CSS !== 'undefined' && CSS.escape)
                ? CSS.escape(draggedId)
                : draggedId.replace(/(["'\\\\\[\]])/g, '\\\\$1');
            draggedEl = document.querySelector(`[data-draggable_id="${safeSel}"]`)
                || document.querySelector(`[data-encounter_creature_id="${safeSel}"]`);
        }
    }

    // Map-page drags originate from `.ft-marker-token` overlays on the
    // Leaflet pins on view_map.php. They must ignore global sidelog context:
    // target/campaign/encounter state is now helper-backed and may describe
    // the active encounter UI, not the map pin being dragged. Using it here
    // can move whichever creatures are currently targeted instead of the
    // token the user actually dropped, or refresh only the destination pin.
    //
    // For map-page drags we therefore rely on the data-* attrs the PHP
    // renderer stamped onto the `.ft-marker-token` itself. For non-map drags
    // (sidelog GM tool, etc.) we keep the existing behaviour: active context
    // first, dataset as
    // fallback.
    const isMapPageMove = !!(draggedEl && draggedEl.classList && draggedEl.classList.contains('ft-marker-token'));
    const context = resolveEncounterContext({
        encounterId: draggedEl?.dataset?.gmSourceEncounterId || draggedEl?.dataset?.encounter_source_id || '',
    });

    const campaign_id = isMapPageMove
        ? (draggedEl?.dataset?.encounter_campaign_id || '')
        : (context.campaign_id || draggedEl?.dataset?.encounter_campaign_id || resolveCampaignIdFromCache());
    const encounter_id = isMapPageMove
        ? (draggedEl?.dataset?.encounter_source_id || '')
        : (context.encounter_id || draggedEl?.dataset?.encounter_source_id || '');

	// A drag is already an explicit target-bound action. Never reinterpret another interaction's
	// transient target draft as a legacy multi-creature move.
	const targets_moved = draggedId;

	// Diagnostic — surfaces in the console when the user reproduces a drop.
	// `dropzoneId` is the DESTINATION encounter id (from the dropzone the
	// user released over). `encounter_id` is the SOURCE encounter id. If
	// these come back wrong (empty, swapped, pointing at an unexpected
	// encounter) the move will silently land on a different encounter and
	// the dragged token will appear to "vanish" — log them here so we can
	// catch that fast next time something goes sideways.
	if (isMapPageMove) {
		ftDebugLog('xz dnd', {
			encounterCreaturesMovedMapPage: {
				draggedId,
				dropzoneId,
				source_encounter_id: encounter_id,
				campaign_id,
				targets_moved,
				draggedElDataset: draggedEl ? { ...draggedEl.dataset } : null,
			},
		});
	}

	if (draggedId) {
		// Original GM-sidelog path uses gmSourceEncounterId on the card;
		// the marker-popup path stamps encounter_source_id instead, so
		// honor either when guarding against same-encounter drops.
		const sourceEnc =
			draggedEl?.dataset?.gmSourceEncounterId
			|| draggedEl?.dataset?.encounter_source_id
			|| '';
		if (sourceEnc && dropzoneId === sourceEnc) {
			displayWarning('That creature is already in this encounter.');
			return;
		}
	}


	const input_hidden_encounter_link_form = document.getElementById('input_hidden_encounter_link_form');

    const relocate = (window as Window & { ftGeometryRelocation?: (data: Record<string, unknown>) => Promise<Record<string, unknown>> }).ftGeometryRelocation;
    if (!relocate) { refuseUnported('relocation:browser-not-loaded'); return; }
    try {
        const result = await relocate({ campaign_id, encounter_id: dropzoneId, target_id: targets_moved,
            source_encounter_id: encounter_id, prefer_inventory_link_spawn: 1,
            relocation_route: 'gm_move_targets_to_encounter_inc.php' });
        if (result.ok !== true) return;
    } catch (error) { displayWarning(error instanceof Error ? error.message : String(error)); return; }


	// The GM viewer refresh paths only make sense when `div_refresh_gm_viewer`
	// is on the page (sidelog GM tools). Map pages (Leaflet popup drops)
	// have no such element — skip the follow-up so we don't 404.
	const div_refresh_gm_viewer = document.getElementById('div_refresh_gm_viewer');
	if (div_refresh_gm_viewer) {
		if (input_hidden_encounter_link_form) {
			await queryData(
				BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_encounter_links_inc.php",
				'div_refresh_gm_viewer',
				{
					campaign_id,
					encounter_id,
				},
				true, // runPostFunctions
				false // show_loader
			);
		} else {
			await queryData(
				BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_public_encounters_inc.php",
				'div_refresh_gm_viewer',
				{
					campaign_id,
				},
				true, // runPostFunctions
				false // show_loader
			);
		}
	} else {
		// Map-page drop on view_map.php. Two things
		// need to happen here that the GM-sidelog path gets "for free":
		//   1) any open Leaflet popup is closed so it can't shadow the
		//      refreshed roster strips on the pins, and
		//   2) the on-pin roster overlays for both the source and the
		//      destination encounter need to re-render so the moved creature
		//      visually leaves one pin and shows up on the other (without
		//      this they only update on a full page reload).
		try {
			document.querySelectorAll<XzLeafletElement>('.leaflet-container').forEach((el) => {
				const m = el && el._ftLeafletMap;
				if (m && typeof m.closePopup === 'function') {
					m.closePopup();
				}
			});
		} catch (e) { /* ignore */ }

		if (typeof window.refreshMapMarkerRostersForEncounters === 'function') {
			// Pass both ends of the move; the helper de-dups + skips unknowns.
			const affected = [encounter_id, dropzoneId].filter(Boolean);
			try {
				await window.refreshMapMarkerRostersForEncounters(affected);
			} catch (e) { /* helper logs its own warnings */ }
		}
	}

}

async function encounter_linked_creatures_moved(draggedId: string, dropzoneId: string): Promise<void> {
    ftDebugLog('xz dnd', { encounterCreaturesMoved: { draggedId, dropzoneId } });


    // The dragged row is the explicit target; ambient ability/GM drafts are unrelated.
    const srcEl = document.getElementById(draggedId);
    const context = resolveEncounterContext({ encounterId: srcEl?.dataset?.gmSourceEncounterId || '' });
	const campaign_id = context.campaign_id || resolveCampaignIdFromCache();
	const encounter_id = context.encounter_id || srcEl?.dataset?.gmSourceEncounterId || '';

	const targets_moved = draggedId;

	if (draggedId) {
		const sourceEnc = srcEl?.dataset?.gmSourceEncounterId || '';
		if (sourceEnc && dropzoneId === sourceEnc) {
			displayWarning('That creature is already in this encounter.');
			return;
		}
	}


    // MOVE TARGETS
    await queryData(
		BASE_URL + "options/tools/encounters/gm_load_encounter/gm_move_targets_to_encounter_inc.php",
		dropzoneId,
		{
			campaign_id: campaign_id,
			encounter_id: dropzoneId,
			target_id: targets_moved,
			source_encounter_id: encounter_id,
			prefer_inventory_link_spawn: 1,
		},
		false,
		false
	);


	await queryData(
		BASE_URL + "options/tools/encounters/gm_load_encounter/form_query_campaign_encounter_links_inc.php",
		'div_refresh_gm_viewer',
		{
			campaign_id,
			encounter_id,
		},
		false,
		true
	);

}

/** N6 / N4 ask 1: a move whose C1 claim the server refused as STALE (nothing written) is retried ONCE after the encounter resync, with a
 *  freshly compiled claim — never a second time. */
async function moveInventoryWithReceipt(...args: Parameters<typeof moveInventoryWithReceiptOnce>): Promise<InventoryMoveReceipt | InventoryMoveRefusal | null> {
    try {
        return await moveInventoryWithReceiptOnce(...args);
    } catch (error) {
        if (!(error instanceof InventoryMoveC1Stale)) throw error;
        // MV5 (N6c ask d + coordinator fold): the page's OWN compile refused this claim (N4c's pre-write refusal, `c1-stale:refused:<reason>`)
        // — a refusal, its reason shown, never retried; except the transient `client-timeout` / `def-missing`, retried once below (c1RefusalReason).
        const reason = c1RefusalReason(error.why);
        if (reason !== null) {
            return moveRefusal('c1-refused:' + reason, `That move was not made: this page could not work out its result (${reason}). Reload the page and try again.`, args[12] === true, !args[15]);
        }
        ftDebugLog('ft3d inventory move', { stage: 'c1-stale-retry', why: error.why });
        const encounterId = String(resolveInventoryMoveEncounterId(String(args[3] ?? ''), String(args[0] ?? ''), String(args[4] ?? '')) ?? '');
        const resync = (window as unknown as { ftResyncEncounter?: (id: string) => Promise<void> }).ftResyncEncounter;
        try { if (encounterId && typeof resync === 'function') await resync(encounterId); } catch { /* the retry recompiles from what the page holds */ }
        // N6d: a CARRIED mutation id (args[13]) stays the same for the retry (the chain may retry it again after a lost answer); its
        // settled claim's operation id is not reused for a second claim — the retry posts none (mutation_retry).
        const retryArgs = [...args] as Parameters<typeof moveInventoryWithReceiptOnce>;
        if (typeof args[13] === 'string' && checkIfValidUUID(args[13])) retryArgs[14] = true;
        try {
            return await moveInventoryWithReceiptOnce(...retryArgs);
        } catch (again) {
            if (!(again instanceof InventoryMoveC1Stale)) throw again;
            if (again.why.startsWith('c1-stale:refused:')) {
                // the retry refused too (any reason, a transient one included): a refusal now, its words shown
                const reason = again.why.slice('c1-stale:refused:'.length);
                return moveRefusal('c1-refused:' + reason, `That move was not made: this page could not work out its result (${reason}). Reload the page and try again.`, args[12] === true, !args[15]);
            }
            displayWarning('That inventory changed elsewhere — try the move again.');
            return null;
        }
    }
}

async function moveInventoryWithReceiptOnce(
    inventory_id: unknown,
    item_qty: unknown,
    move_from_inventory_id: unknown,
    move_to_possess_id: unknown,
    move_type: unknown,
    show_log_message: unknown,
    x_pos: unknown = null,  // New optional param
    z_pos: unknown = null,  // New optional param
    ft_moved: unknown = 0,  // New optional param
    ft3d_move_client_instance_id: unknown = '',
    ft3d_move_sequence: unknown = 0,
    // FT3D scene drops only: the destination ELEVATION (feet). The (x_pos, z_pos) pair above is the
    // scene's GROUND pair; the endpoint routes both by the encounter's axis convention.
    ft3d_elevation: unknown = null,
    quiet = false,
    // N6d (2026-10-02, coordinator Codex review N1b): a caller that may RETRY a move whose answer was lost (the bag-equip chain —
    // inventory-equip-truth.ts equipFromBag) passes ONE mutation id for every attempt, so the endpoint replays the first attempt's
    // receipt (ft_inventory_move_idempotency_*, 90 s) instead of moving a second item off a stack. '' = a fresh id per call, as before.
    mutation_id_in: unknown = '',
    // N6d: true on such a RETRY — no C1 claim is built (its operation id is the first attempt's, whose verdict may already have been
    // published; a second claim under it would wait for a verdict that never comes). The endpoint then rebuilds, as before C1.
    mutation_retry = false,
    // MV5: the bag-equip chain's options (InventoryMoveOptions — required decision, Fast Draw leg, split in place). null = as before.
    move_options: InventoryMoveOptions | null = null,
): Promise<InventoryMoveReceipt | InventoryMoveRefusal | null> {
	// openSideLog();
    if (!(await awaitInventoryBirth(String(inventory_id ?? ''))).ok) return moveRefusal('inventory-birth-refused',
        INVENTORY_BIRTH_WAIT_WORDS, quiet, !move_options);
    const baseOwner = String(window.ftGetActiveCasterId?.() || window.ftGetActiveEncounterId?.() || '');
    if (window.ftRequireInventoryBase && !window.ftRequireInventoryBase(baseOwner)) {
        // A deploy paused this page (base-patch.ts version fence): name the reload, not a missing compile (playtest 2026-10-13).
        return window.ftBaseCommitsPaused?.()
            ? moveRefusal('engine-version-reload', 'A new FateTide version is live. Reload the page to keep moving items.', quiet, false)
            : moveRefusal('unported:inventory-base-unavailable', 'Inventory is not yet compiled in this browser.', quiet, false);
    }

    // Remove overlay if it exists
    removeInventorySliderOverlay();
    ftDebugLog('xz dnd', { moveDraggableInventory: true, inventory_id, move_to_possess_id });

    const campaign_id = resolveCampaignIdFromCache();
    const encounter_id = resolveInventoryMoveEncounterId(move_to_possess_id, inventory_id, move_type);
    const floorId = String(encounter_id ?? '').toUpperCase();
    const holderIdForFloor = ftInventoryExpectedHolder(String(inventory_id ?? '').toUpperCase());
    const floorTransfer = move_type === 'drop_inventory' || (floorId && String(move_to_possess_id ?? '').toUpperCase() === floorId);
    const settle = (window as Window & { ftSettleServerInventoryDrop?: (receipt: InventoryMoveReceipt, context: ServerDropContext,
        project: (receipt: InventoryMoveReceipt) => void) => Promise<InventoryMoveReceipt> }).ftSettleServerInventoryDrop;
    if (floorTransfer && !settle) { refuseUnported('hand-drop:browser-not-loaded'); return null; }
    const floorSource = floorTransfer ? moveRecord(await getProximityEntity(String(inventory_id ?? ''))) : null;
    const floorPickup = !floorTransfer && floorId !== '' && holderIdForFloor === floorId;
    // Pickups use the recipient's feet; DOM floor drops without a scene point use the acting holder's feet.
    if (floorPickup || (floorTransfer && floorSource && String(rowScalar(floorSource, 'possess_id') ?? '').toUpperCase() !== floorId
        && x_pos == null && z_pos == null && ft3d_elevation == null)) {
        const holder = moveRecord(await getProximityEntity(String(floorPickup ? move_to_possess_id : window.ftGetActiveCasterId?.() ?? '')));
        if (holder && (!floorPickup || rowScalar(holder, 'encounter_creature_id'))) {
            const feet = position(holder);
            x_pos = feet.x;
            z_pos = feet.y ?? 0;
            ft3d_elevation = feet.z;
        }
    }
    if (floorTransfer && !mutation_retry && !carriedMoveAttempts.has(String(mutation_id_in).toUpperCase())) {
        const actorId=String(window.ftGetActiveCasterId?.()??'');
        const carried=String(holderIdForFloor||move_from_inventory_id||'').toUpperCase()!==floorId;
        const gm=(window as unknown as {FateTide?:{user?:{isGM?:boolean}};ftActiveViewerIsFullGm?:()=>boolean}).FateTide?.user?.isGM===true
            || (window as Window & {ftActiveViewerIsFullGm?:()=>boolean}).ftActiveViewerIsFullGm?.()===true;
        const editor=typeof ftIsModifyEncounterToolsPage==='function' && ftIsModifyEncounterToolsPage();
        if(carried && !gm && !editor){
            const verdict=decideInventoryMove({casterId:actorId,inventoryId:String(inventory_id),qty:Number(item_qty??1),moveToPossessId:floorId,
                moveType:'drop_inventory',encounterId:floorId,campaignId:String(campaign_id??''),isGm:gm,editorSurface:editor,xPos:x_pos,zPos:z_pos,elevation:ft3d_elevation});
            if(!('decision'in verdict)||verdict.decision.target!==null){
                const reason='refuse'in verdict?verdict.refuse:'decision'in verdict?'hand-drop:stack-merge-unported':`hand-drop:${'handback'in verdict?verdict.handback:'doff-required'}`;
                refuseUnported(reason, undefined, undefined, 'refuse' in verdict ? 'hand-drop:inventory-move-refused' : undefined);return moveRefusal(reason,reason,quiet,false);
            }
        }
        const bridge=(window as Window & {ftTryCommitFloorInventoryMove?:(r:{encounterId:string;inventoryId:string;x:number;y:number;z:number;shape?:unknown;wholeRow?:boolean;quantity?:number;handDrop?:{actorId:string;quantity:number}},legacy:()=>void)=>Promise<'handled'|'refused'|null>}).ftTryCommitFloorInventoryMove;
        const frame=document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        const shape=(frame?.contentWindow as (Window&{ft3dGeometryShape?:()=>unknown})|null)?.ft3dGeometryShape?.();
        if(!bridge){refuseUnported('hand-drop:browser-not-loaded');return null;}
        const depthCoordinates=ft3d_elevation!==null&&ft3d_elevation!==undefined&&Number.isFinite(Number(ft3d_elevation));
        const outcome=await bridge({encounterId:floorId,inventoryId:String(inventory_id),x:Number(x_pos??0),y:depthCoordinates?Number(z_pos??0):0,z:Number(depthCoordinates?ft3d_elevation:z_pos??0),shape,wholeRow:true,quantity:Number(item_qty??1),
            ...(carried?{handDrop:{actorId,quantity:Number(item_qty??1)}}:{})},()=>{});
        if(outcome===null)refuseUnported('hand-drop:floor-plan-unavailable');
        return null;
    }
    const caster_id = window.ftGetActiveCasterId?.() ?? '';
    const modify_encounter_tools = typeof ftIsModifyEncounterToolsPage === 'function'
        && ftIsModifyEncounterToolsPage()
        ? 1
        : 0;
    const gm = (window as unknown as { FateTide?: { user?: { isGM?: boolean } } }).FateTide?.user?.isGM === true;
    if (!modify_encounter_tools && !gm) {
        const within = await inventoryMoveProximity(String(caster_id), encounter_id, String(inventory_id ?? ''),
            String(move_to_possess_id ?? ''), String(move_type ?? ''),
            // objectReachFt: Mental Grip's object_interaction_range_ft reaches unattended floor objects / floor containers (2026-10-07).
            { entity: getProximityEntity, reachable: actorCanReachTarget, objectReachFt: actorObjectInteractionRangeFt });
        if (within !== true) {
            // D-020 (owner 2026-09-30): a give also needs reach to the receiving creature (inventoryMoveProximity).
            displayWarning(within === false ? 'Move closer to take from, place into, or hand over that inventory.' : 'Could not determine reach. Refresh the encounter and try again.');
            return null;
        }
        if (String(window.ftGetActiveCasterId?.() ?? '') !== String(caster_id)) return null;
    }
    // fix-bug 2026-10-25 (a Ring, Wood dropped on the paper doll landed in the OFF HAND): an equip — the doll's drop, throw-object's draw —
    // is the equip envelope's (equipInventory: resolveEquipSlot / resolveEquipSwap plan the slot; a stack or bagged row runs its own chain).
    // Posted to the move endpoint, the slot was PHP's resolve_best_equipment_slot over get_inventory_arr()'s raw base — no item_layer_types,
    // so no equipment_slot — and every item went to the off hand.
    if (move_type === 'equip_inventory') {
        const equip = (window as unknown as { ftEquipInventory?: (inventoryId: string, actorId?: string | null) =>
            Promise<{ ok: boolean; status: number; local?: boolean; message?: string }> }).ftEquipInventory;
        if (typeof equip !== 'function') { refuseUnported('equip:browser-not-loaded'); return null; }
        const actorId = checkIfValidUUID(String(move_to_possess_id ?? '')) ? String(move_to_possess_id) : String(caster_id ?? '');
        const answer = await equip(String(inventory_id ?? ''), actorId);
        ftDebugLog('ft3d inventory move', { stage: 'equip-envelope', answer });
        if (!answer.ok) return moveRefusal('equip', answer.message || (answer.status === 409 ? 'Your equipment changed elsewhere — try again.'
            : answer.status === 403 ? 'You cannot equip that.' : 'The equip could not be saved.'), quiet, !move_options);
        // equipOrLegacy's refresh: a `local` answer already repainted the page.
        if (!answer.local) await (window as unknown as { refreshCasterSheet?: (id: string) => unknown }).refreshCasterSheet?.(actorId);
        return null;
    }
    const input_hidden_sidelog_encounter_creature = document.getElementById('input_hidden_sidelog_encounter_creature') as HTMLInputElement | null;
    const input_hidden_sidelog_object_item_id = document.getElementById('input_hidden_sidelog_object_item_id') as HTMLInputElement | null;
	// I2/I3 S3b: the BROWSER decides the move (move-rules.ts) — the endpoint then only permits, CASes and persists. A refusal is the rule's
	// own message and posts nothing; unreadable context refuses before posting.
	let client_decision: ClientMoveDecision | null = null;
	let doff = false;
    let decisionUnavailable = 'browser-plan-unavailable';
	const required = move_options?.requiredDecision ?? null;
	const splitInPlace = move_options?.splitInPlace === true;
	// MV5 (Codex review P2 ×2): a RETRY of a carried mutation id (its first answer was lost — the move may have committed) re-sends the FIRST
	// attempt's decision and row CAS verbatim instead of re-deciding from the page's newer state: the endpoint then replays the saved
	// receipt (the same body), a changed AP / holder can not turn a committed move into a refusal, and `ap_charged` is the original charge.
	const carriedId = typeof mutation_id_in === 'string' && checkIfValidUUID(mutation_id_in) ? mutation_id_in.toUpperCase() : '';
	const rememberedEntry = carriedId ? carriedMoveAttempts.get(carriedId) ?? null : null;
	const remembered = rememberedEntry && Date.now() - rememberedEntry.at <= CARRIED_ATTEMPT_TTL_MS ? rememberedEntry : null;
	const leg = carriedMoveLeg(move_options);
	// MV2 (Codex round 11 P2): a retry that now REQUIRES another decision (or another move kind) than the remembered first attempt would be
	// answered with the first attempt's move — refuse it, nothing posted.
	if (remembered && !carriedRetryMatches(remembered, required, leg)) {
	    ftDebugLog('ft3d inventory move', { stage: 'carried-retry-mismatch', required, remembered: remembered.decision, leg, rememberedLeg: remembered.leg });
	    return moveRefusal('decision-changed', 'That was not moved: this retry asks for a different move than the first attempt.', quiet, false);
	}
	if (remembered) client_decision = remembered.decision;
	else try {
        const decide = () => decideInventoryMove({ casterId: String(caster_id ?? ''), inventoryId: String(inventory_id ?? ''), qty: Number(item_qty ?? 1),
            moveToPossessId: String(move_to_possess_id ?? ''), moveType: String(move_type ?? ''), encounterId: String(encounter_id ?? ''),
            isGm: gm, editorSurface: modify_encounter_tools === 1, xPos: x_pos, zPos: z_pos, elevation: ft3d_elevation, campaignId: String(campaign_id ?? ''),
            ...(move_options?.apLeg ? { apLeg: move_options.apLeg } : {}), ...(splitInPlace ? { splitInPlace: true } : {}) });
        let verdict = decide();
        if ('handback' in verdict && ['doff-context', 'combat-unknown', 'no-caster-row'].includes(verdict.handback)) {
            const load = (window as unknown as { ftLoadTimedEquipContext?: (id: string) => Promise<unknown> }).ftLoadTimedEquipContext;
            if (caster_id && load) {
                try { await load(String(caster_id)); }
                catch (error) { return moveRefusal('context-unavailable', error instanceof Error ? error.message : String(error), quiet, false); }
                verdict = decide();
            }
        }
	    if ('refuse' in verdict) return moveRefusal('rule', verdict.refuse, quiet, !move_options);
	    if ('decision' in verdict) client_decision = verdict.decision;
	    else if ('doff' in verdict) doff = true;
	    else { decisionUnavailable = verdict.handback; ftDebugLog('ft3d inventory move', { stage: 'client-decision-handback', why: verdict.handback }); }
	} catch (error) { decisionUnavailable = 'decision-error'; ftDebugLog('ft3d inventory move', { stage: 'client-decision-error', error: String(error) }); client_decision = null; }
	if (floorTransfer && !client_decision && !doff) {
	    refuseUnported('hand-drop:floor-plan-unavailable'); return null;
	}
	if (!client_decision && !doff) {
	    refuseUnported(`inventory-move:${decisionUnavailable}`); return null;
	}
	// MV5 (N6c ask a / the split leg): a caller bound to a decision, a split and a Fast Draw leg move ONLY as the page decided — never a
	// hand-back to PHP's own decision (which could merge the item, or charge a different AP).
	if (!remembered && (required || splitInPlace || move_options?.apLeg === 'fast-draw') && !client_decision) {
	    return moveRefusal('undecidable', 'That was not moved: this page cannot tell where it would end up. Drag it onto an equipment slot instead.', quiet, false);
	}
	if (!remembered && required && !sameMoveDecision(client_decision, required)) {
	    ftDebugLog('ft3d inventory move', { stage: 'required-decision-changed', required, decided: client_decision });
	    return moveRefusal('decision-changed', client_decision && client_decision.target !== null && required.target === null
	        ? 'That was not moved: it would now join a stack you carry.'
	        : 'That was not moved: your inventory or AP changed — try again.', quiet, false);
	}
	// Donning lane (2026-09-29): a move of worn gear with a doffing time STARTS doffing — the equip envelope's `doff` kind (the page decides the
	// time, the status, the AP; the page-observed finish moves the item). A failed start sends no move request.
	if (doff) {
	    const commit = (window as unknown as { ftCommitInventoryEquip?: (i: { kind: 'doff'; actorId?: string; inventoryId?: string }) =>
	        Promise<{ ok: boolean; handback?: boolean; status: number; message?: string; error?: string }> }).ftCommitInventoryEquip;
	    const answer = typeof commit === 'function'
	        ? await commit({ kind: 'doff', actorId: String(caster_id ?? ''), inventoryId: String(inventory_id ?? '') }).catch(() => null)
	        : null;
	    ftDebugLog('ft3d inventory move', { stage: 'client-decision-doff', answer });
	    if (!answer?.ok) displayWarning(answer?.message || (answer?.status === 409 ? 'Your equipment changed elsewhere — try again.' : 'The doff could not be started.'));
	    return null;
	}
	const mutation_id = carriedId || ftInventoryMoveMutationId();
	const sink = ftCurrentTruthSink();
    if (!remembered && client_decision?.tailsRequired) {
        const plan = (window as unknown as { ftPlanInventoryMoveTails?: (input: { encounterId: string; actorId: string; inventoryId: string;
            destination: string; quantity: number; merge: string | null; operationId: string; apAfter: number | null }) => Promise<InventoryMoveTails> }).ftPlanInventoryMoveTails;
        try {
            if (sink) throw new Error('inventory-move:memory-transport-tails-not-ported');
            if (!plan) throw new Error('inventory-move:transport-plan-unavailable');
            client_decision.tails = await withAsyncDeadline(() => plan({ encounterId: String(encounter_id), actorId: String(caster_id),
                inventoryId: String(inventory_id), destination: client_decision!.destination?.possess_id ?? String(move_to_possess_id),
                quantity: Number(item_qty ?? 1), merge: client_decision!.target, operationId: mutation_id, apAfter: client_decision!.ap.after }), 25000);
        } catch (error) {
            const reason = (error as { name?: string })?.name === 'TimeoutError' ? 'inventory-move:tail-plan-timeout'
                : String((error as { reason?: string })?.reason ?? (error instanceof Error ? error.message : error));
            refuseUnported(reason.startsWith('inventory-move:') ? reason : `inventory-move:tail-plan:${reason}`);
            return moveRefusal(reason, reason, true, false);
        }
    }
    if (sink) {
        if (!client_decision || !sink.moveInventory || move_type !== 'move_inventory') {
            const reason = `inventory-move:${!sink.moveInventory ? 'memory-save-unavailable' : move_type !== 'move_inventory' ? String(move_type) : decisionUnavailable}`;
            refuseUnported(reason); return moveRefusal(reason, reason, true, false);
        }
        try {
            const receipts = await sink.moveInventory({ operationId: mutation_id, encounterId: String(encounter_id), actorId: String(caster_id),
                destinationId: String(move_to_possess_id), combat: client_decision.ap.in_combat, beforeAp: client_decision.ap.before,
                entries: [{ inventoryId: String(inventory_id), quantity: Number(item_qty ?? 1), target: client_decision.target, ap: client_decision.ap.cost }] });
            const receipt = receipts[0]!;
            ftPublishLocalInventoryMoves(receipts);
            return receipt;
        } catch (error) {
            if (!isTruthSinkRefusal(error)) throw error;
            refuseUnported(error.reason); return moveRefusal(error.reason, error.reason, true, false);
        }
    }
    if (client_decision?.destination) {
        move_to_possess_id = client_decision.destination.possess_id;
        x_pos = client_decision.destination.x; z_pos = client_decision.destination.y; ft3d_elevation = client_decision.destination.z;
        if (String(move_to_possess_id).toUpperCase() === floorId && client_decision.target === null) {
            // A loose floor target resolves to its surrounding floor. Reuse the native placement
            // entry before any save; a selected merge below changes quantities only.
            return moveInventoryWithReceiptOnce(inventory_id, item_qty, move_from_inventory_id, move_to_possess_id,
                move_type, show_log_message, x_pos, z_pos, ft_moved, ft3d_move_client_instance_id, ft3d_move_sequence,
                ft3d_elevation, quiet, mutation_id_in, mutation_retry, move_options);
        }
    }
	const expected_possess_id = remembered ? remembered.expectedHolder : ftInventoryExpectedHolder(String(inventory_id ?? '').toUpperCase());
	if (carriedId && !remembered) rememberCarriedMoveAttempt(carriedId, client_decision, expected_possess_id, leg);
	const requestStartedAt = performance.now();
	ftSetInventoryMovePending(String(inventory_id ?? ''), mutation_id, true);
    // Janus C1: pickup (floor → creature) / drop (creature → floor) build the shadow envelope from the rows the browser holds;
    // C1b (plan §9.1): a give (and a creature→creature move_inventory "take") builds the TWO-carrier pair envelope.
    // Never blocks the move: any failure inside yields a small unsupported/error envelope or null.
    let receipt: InventoryMoveReceipt | null;
    try {
		receipt = await ftPostInventoryMove({
            campaign_id,
            encounter_id,
            caster_id,
            modify_encounter_tools,
            inventory_id,
            item_qty,
            move_from_inventory_id,
            move_to_possess_id,
            move_type,
            show_log_message,
            x_pos,
            z_pos,
            ft_moved,
            ft3d_move_client_instance_id,
            ft3d_move_sequence,
            // I2/I3 S3: the row CAS — the holder this page moved the row FROM.
            ...(expected_possess_id ? { expected_possess_id } : {}),
            // I2/I3 S3b: the browser's decision (merge target, AP + combat verdict) — the endpoint skips its own rules for it.
            ...(client_decision ? { client_decision } : {}),
            ...(Number.isFinite(Number(ft3d_elevation)) && ft3d_elevation !== null && ft3d_elevation !== ''
                ? { ft3d_elevation: Number(ft3d_elevation) }
                : {}),
            receipt_version: 1,
            // Stowing changes climb eligibility; publish the new hand counts before the next move.
            ...(quiet || (move_type === 'move_inventory' && !!move_from_inventory_id
                && move_from_inventory_id === move_to_possess_id) ? { await_inventory_reconcile: 1 } : {}),
            mutation_id,
        }, carriedId);
    } catch (error) {
        ftSetInventoryMovePending(String(inventory_id ?? ''), mutation_id, false);
        // MV5 (Codex round 2 P2): an ANSWERED pre-write rejection (a refused / stale claim, a 4xx) wrote nothing — the next attempt under
        // this id re-decides from the page's current state. Only a lost answer (transport / 5xx) keeps the first attempt's body for replay.
        // MV3 (coordinator round 18 P3): a 409 `mutation_in_progress` (the first attempt may still be writing) or `mutation_id_conflict` (the id's
        // owner is another body) is NOT an answered rejection of THIS attempt — keep the first body so a later retry replays it verbatim.
        if (carriedId && (error instanceof InventoryMoveC1Stale || (error instanceof InventoryMoveHttpError && error.status >= 400 && error.status < 500
            && !['mutation_in_progress', 'mutation_id_conflict'].includes(error.code)))) {
            carriedMoveAttempts.delete(carriedId);
        }
        if (error instanceof InventoryMoveC1Stale) {
            // Settle the refused claim like a not-served verdict; the wrapper resyncs and retries once with a fresh claim.
            throw error;
        }
        console.error('FT3D inventory move failed:', error);
        return null;
    }
    // C1k: keep the page's carrier-catalog mirror and definition epoch current from the receipt (no fetch on the next claim).
	if (receipt === null) {
		ftSetInventoryMovePending(String(inventory_id ?? ''), mutation_id, false);
		ftDebugLog('ft3d inventory move', {
			mutationId: mutation_id,
			stage: 'accepted-deferred-without-inventory-receipt',
			requestMs: Math.round((performance.now() - requestStartedAt) * 10) / 10,
		});
	} else {
        const tailsFrame = client_decision?.tails?.appliedFrame;
        if (tailsFrame && !receipt.browser_applied) await window.ftApplyBasePatch?.({ type: 'base_patch', encounter_id: String(encounter_id), ops: [], applied: tailsFrame });
        // The saved native decision is evidence for crediting a bag draw against the following equip AP.
        if (client_decision) {
            const charged = client_decision.ap.in_combat ? client_decision.ap.cost : 0;
            receipt.ap_charged = { cost: charged, in_combat: client_decision.ap.in_combat, before: client_decision.ap.before,
                after: client_decision.ap.after, source: 'page-decision' };
        }
        const responseAt = performance.now();
        let changedRoots = 0;
        const project = (saved: InventoryMoveReceipt): void => {
            if (saved.browser_applied) window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt-applied', { detail: saved }));
            else changedRoots = ftApplyInventoryMoveReceipt(saved);
        };
        const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
        const shape = (frame?.contentWindow as (Window & { ft3dGeometryShape?: () => unknown }) | null)?.ft3dGeometryShape?.();
        if (settle) receipt = await settle(receipt, { actorId: String(caster_id || '') || null, moveType: String(move_type), shape }, project);
        else project(receipt);
        ftSetInventoryMovePending(String(inventory_id ?? ''), mutation_id, false);
        ftDebugLog('ft3d inventory move', {
            mutationId: receipt.mutation_id,
            stage: 'receipt-applied',
            requestMs: Math.round((responseAt - requestStartedAt) * 10) / 10,
            serverCommitMs: receipt.timing?.commit_ms ?? null,
            changedRoots,
            outcome: receipt.move.outcome,
        });
	}

	// Handle sidelog creature refresh if applicable
    if (quiet) return receipt;
    if(
        input_hidden_sidelog_encounter_creature
    ){
        const input_hidden_sidelog_encounter_creature_id = input_hidden_sidelog_encounter_creature.value;
        await openItemInfo(
			input_hidden_sidelog_encounter_creature_id,
			false,
			__ftShouldQuietRefreshSideInfoPanel(),
		);
    }

	// Handle sidelog creature refresh if applicable
    if(
        input_hidden_sidelog_object_item_id
    ){
        const input_hidden_sidelog_object_item_id_value = input_hidden_sidelog_object_item_id.value;
        await openItemInfo(
			input_hidden_sidelog_object_item_id_value,
			false,
			__ftShouldQuietRefreshSideInfoPanel(),
		);
    }
    return receipt;
}

async function takeAllInventoryWithReceipt(source: string, destination: string,
    entries: { inventory_id: string; item_qty: number }[]): Promise<number> {
    const caster = String(window.ftGetActiveCasterId?.() ?? '');
    const encounter = resolveInventoryMoveEncounterId(destination, entries[0]?.inventory_id, 'move_inventory');
    const within = source.toUpperCase() === String(encounter).toUpperCase()
        ? (await Promise.all(entries.map(entry => inventoryMoveProximity(caster, encounter, entry.inventory_id, destination, 'move_inventory',
            { entity: getProximityEntity, reachable: actorCanReachTarget, objectReachFt: actorObjectInteractionRangeFt })))).every(value => value === true)
        : await actorCanReachTarget(caster, source);
    if (within !== true) {
        displayWarning(within === false ? 'Move closer to take inventory.' : 'Could not determine reach. Refresh the encounter and try again.');
        return 0;
    }
    // I2/I3 S4c: the BROWSER decides the batch (capacity chain, carry maximum, keyring, each row's stack in order, AP); a refusal is the
    // rule's own message and posts nothing; unreadable context refuses before posting.
    // D-021 (owner 2026-09-30): in combat the page takes the leading rows the actor's AP pays for (the legacy partial haul) and posts only
    // those; `short` is the rule's message for the row that stopped it.
    let client_decision: ClientTakeAllDecision | null = null;
    let apShort: string | null = null;
    let decisionUnavailable = 'browser-plan-unavailable';
    try {
        const verdict = decideTakeAll({ casterId: caster, sourceId: source, destinationId: destination, encounterId: String(encounter ?? ''), entries });
        if ('refuse' in verdict) { displayWarning(verdict.refuse); return 0; }
        if ('decision' in verdict) { client_decision = verdict.decision; entries = verdict.entries; apShort = verdict.short?.message ?? null; }
        else { decisionUnavailable = verdict.handback; ftDebugLog('ft3d inventory take all', { stage: 'client-decision-handback', why: verdict.handback }); }
    } catch (error) { decisionUnavailable = 'decision-error'; ftDebugLog('ft3d inventory take all', { stage: 'client-decision-error', error: String(error) }); client_decision = null; }
    if (!client_decision) { refuseUnported(`inventory-move:take-all:${decisionUnavailable}`); return 0; }
    const sink = ftCurrentTruthSink();
    if (sink) {
        if (!client_decision || !sink.moveInventory) { refuseUnported(`inventory-move:take-all:${sink.moveInventory ? decisionUnavailable : 'memory-save-unavailable'}`); return 0; }
        try {
            const decision = client_decision;
            const receipts = await sink.moveInventory({ operationId: ftInventoryMoveMutationId(), encounterId: encounter, actorId: caster,
                destinationId: destination, combat: decision.ap.in_combat, beforeAp: decision.ap.before,
                entries: entries.map(entry => ({ inventoryId: entry.inventory_id, quantity: entry.item_qty,
                    target: decision.rows[entry.inventory_id.toUpperCase()]!.target, ap: decision.rows[entry.inventory_id.toUpperCase()]!.ap })) });
            ftPublishLocalInventoryMoves(receipts);
            if (apShort) displayWarning(apShort);
            return receipts.length;
        } catch (error) {
            if (!isTruthSinkRefusal(error)) throw error;
            refuseUnported(error.reason); return 0;
        }
    }
    // One request, no client possessor compile per row and no automatic replay of a batch.
    const mutation_id = ftInventoryMoveMutationId();
    const response = await withAsyncDeadline(() => fetch(BASE_URL + 'options/tools/inventory/ft3d/move_draggable_inventory_inc.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_id: resolveCampaignIdFromCache(), encounter_id: encounter,
            caster_id: caster, move_from_inventory_id: source, move_to_possess_id: destination,
            move_type: 'move_inventory', show_log_message: true, take_all_entries: entries, mutation_id,
            ...(client_decision ? { client_decision } : {}) }),
    }), 20_000);
    const body = await withAsyncDeadline(() => response.text(), 20_000);
    let parsed: unknown;
    try { parsed = JSON.parse(body); } catch { parsed = null; }
    const batch = moveRecord(parsed);
    if (!response.ok || batch?.schema !== 'ft3d.inventory-take-all-receipt.v1' || batch.ok !== true || !Array.isArray(batch.moves)) {
        ftRenderInventoryMoveFailure(body, 'Take All could not be confirmed. Review the remaining inventory.');
        throw new Error('Take All did not return a valid receipt');
    }
    const roots = ftInventoryMoveCachedRoots();
    const receipts: InventoryMoveReceipt[] = [];
    for (const [index, move] of batch.moves.entries()) {
        const receipt: InventoryMoveReceipt = { schema: 'ft3d.inventory-move-receipt.v1', ok: true,
            mutation_id: `${mutation_id}:${index}`, move, affected_caster_ids: [caster],
            affected_encounter_ids: [encounter], timing: { accepted_unix_ms: Date.now(), commit_ms: 0 } };
        receipt.member_revs = batch.member_revs as Record<string, number> | undefined;
        if (!ftIsInventoryMoveReceipt(receipt)) throw new Error('Invalid Take All move');
        receipts.push(receipt);
    }
    await publishInventoryMoveBatch(receipts, { mutation_id, encounter_id: encounter, caster_id: caster,
        move_type: 'move_inventory', client_decision }, response);
    for (const receipt of receipts) {
        if (!receipt.browser_applied) applyInventoryMoveReceiptToRoots(receipt, roots);
        window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt-applied', { detail: receipt }));
    }
    // The caller refreshes the final weights and paints once for the entire receipt.
    window.dispatchEvent(new CustomEvent('ft:inventory-take-all-receipt-applied', { detail: batch }));
    if (apShort) displayWarning(apShort);
    return batch.moves.length;
}

async function move_draggable_inventory(...args: Parameters<typeof moveInventoryWithReceipt>): Promise<void> {
    await moveInventoryWithReceipt(...args);
}

// ===========================================================================================
// Window attachment — preserve the exact public surface the originals exposed in template.js.
// The encounter-map bundle (DragManager / AOEManager / AOETargetResolver / XZRuler) reaches these
// as bare-identifier globals at runtime; template.js's showInventorySliderPopup confirm path calls
// window.move_draggable_inventory. MAP_MARKER_INVENTORY_TEMPLATE_IDS stays module-local.
// ===========================================================================================
declare global {
    interface Window {
        encounter_creatures_moved: typeof encounter_creatures_moved;
        encounter_linked_creatures_moved: typeof encounter_linked_creatures_moved;
        move_draggable_inventory: typeof move_draggable_inventory;
        gmMoveCreatureCrossEncounterAtGrid: typeof gmMoveCreatureCrossEncounterAtGrid;
        refreshGmViewerAfterCreatureMove: typeof refreshGmViewerAfterCreatureMove;
        getGmDragSourceEncounterId: typeof getGmDragSourceEncounterId;
        getAoeSpatialReferenceEncounterCreatureId: typeof getAoeSpatialReferenceEncounterCreatureId;
        inventoryItemElIsExcludedFromAoe: typeof inventoryItemElIsExcludedFromAoe;
    }
}

Object.assign(window, {
    ftTakeAllInventoryWithReceipt: takeAllInventoryWithReceipt,
    ftMoveInventoryWithReceipt: moveInventoryWithReceipt,
    encounter_creatures_moved,
    encounter_linked_creatures_moved,
    move_draggable_inventory,
    gmMoveCreatureCrossEncounterAtGrid,
    refreshGmViewerAfterCreatureMove,
    getGmDragSourceEncounterId,
    getAoeSpatialReferenceEncounterCreatureId,
    inventoryItemElIsExcludedFromAoe,
});

export {};
